import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getQuoteV2ConfigurationQuery,
  QuotesV2QueryKey
} from 'Services/Query/QuotesV2';
import { updateQuoteV2ConfigurationMutation } from 'Services/Mutations/QuotesV2';
import type { QuoteV2Configuration } from 'Services/Query/QuotesV2/Types';

// =============================================================================
// Context Type
// =============================================================================

interface QuoteV2ConfigContextValue {
  /** Full configuration object */
  configuration: QuoteV2Configuration | undefined;
  isLoading: boolean;
  isUpdating: boolean;
  /** Get a single value from the configuration by key */
  getConfigValue: <K extends keyof QuoteV2Configuration>(
    key: K
  ) => QuoteV2Configuration[K] | undefined;
  /** Patch the configuration and update the cache */
  updateConfiguration: (
    body: Partial<QuoteV2Configuration>
  ) => Promise<QuoteV2Configuration>;
}

// =============================================================================
// Context
// =============================================================================

const QuoteV2ConfigContext = createContext<QuoteV2ConfigContextValue | null>(
  null
);

// =============================================================================
// Provider
// =============================================================================

interface QuoteV2ConfigurationProviderProps {
  children: ReactNode;
}

export const QuoteV2ConfigurationProvider = ({
  children
}: QuoteV2ConfigurationProviderProps) => {
  const queryClient = useQueryClient();

  const { data: configuration, isLoading } = useQuery(
    getQuoteV2ConfigurationQuery()
  );

  const { mutateAsync, isLoading: isUpdating } = useMutation({
    ...updateQuoteV2ConfigurationMutation(),
    onSuccess: updated => {
      queryClient.setQueryData([QuotesV2QueryKey.GET_CONFIGURATION], updated);
    }
  });

  const getConfigValue = useCallback(
    <K extends keyof QuoteV2Configuration>(key: K) => configuration?.[key],
    [configuration]
  );

  const updateConfiguration = useCallback(
    (body: Partial<QuoteV2Configuration>) =>
      mutateAsync(body) as Promise<QuoteV2Configuration>,
    [mutateAsync]
  );

  const value = useMemo(
    () => ({
      configuration,
      isLoading,
      isUpdating,
      getConfigValue,
      updateConfiguration
    }),
    [configuration, isLoading, isUpdating, getConfigValue, updateConfiguration]
  );

  return (
    <QuoteV2ConfigContext.Provider value={value}>
      {children}
    </QuoteV2ConfigContext.Provider>
  );
};

// =============================================================================
// Hook
// =============================================================================

export const useQuoteV2Configuration = () => {
  const context = useContext(QuoteV2ConfigContext);

  if (!context) {
    throw new Error(
      'useQuoteV2Configuration must be used within QuoteV2ConfigurationProvider'
    );
  }

  return context;
};
